import React, { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { Autocomplete, Box, Tab } from '@mui/material'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { categoryService } from '../../../services/categoryService'
import { adminProcessService } from '../../../services/admin/process'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import { adminTeamsService } from '../../../services/admin/teams'
import { styled } from '@mui/system'
import { useSelectedProcess } from '../adminStore'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import ProtectedFieldSettingUI from './ProtectedFieldSettingUI'
import { colors } from '../../../theme/colors'

export const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  fontWeight: 'bold',
  backgroundColor: theme.palette.mode === 'light' ? '#f7adff' : '#f7adff',
}))

export const GroupItems = styled('ul')({
  padding: 0,
})

export const textFieldStyle = {
  '& label.Mui-focused': {
    color: colors.button,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'black',
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.button,
      borderWidth: '2px',
    },
  },
}

const CreateProcessTemplateDialog = ({ show, setShow, reloadProcesses }) => {
  const { user } = useAuthContext()
  const [categories, setCategories] = useState([])
  const [teams, setTeams] = useState([])
  const setSelectedProcess = useSelectedProcess((state) => state.setSelectedProcess)
  const selectedProcess = useSelectedProcess((state) => state.selectedProcess)
  const [selectedTabValue, setSelectedTabValue] = React.useState('1')
  const [processTemplate, setProcessTemplate] = useState({
    is_start: false,
    process_template_name: '',
    description: '',
    prefix: '',
  })

  useEffect(() => {
    const allowedViewerTeams = selectedProcess?.allowed_viewer_team_ids
      ?.split(',')
      .map((teamId) => teams.find((team) => team.id == teamId))
      .filter((it) => it != undefined)

    const allowedInitiatorTeams = selectedProcess?.allowed_initiator_team_ids
      ?.split(',')
      .map((teamId) => teams.find((team) => team.id == teamId))
      .filter((it) => it != undefined)

    const selectedCategories = selectedProcess?.category_ids
      ?.map((catId) => categories.find((cat) => cat.value == catId))
      .filter((it) => it != null || it != undefined)

    const initial = {
      is_start: false,
      process_template_name: '',
      description: '',
      prefix: '',
      ...selectedProcess,
      allowed_viewer_team_ids: allowedViewerTeams,
      allowed_initiator_team_ids: allowedInitiatorTeams,
      selected_categories: selectedCategories,
      is_tasks_hidden: selectedProcess?.is_tasks_hidden ? 'No' : 'Yes',
    }

    setProcessTemplate(initial)
  }, [selectedProcess, categories])

  useEffect(() => {
    if (!user) {
      return
    }
    ;(async () => {
      // fetch all team
      const teams = await adminTeamsService.getTeams(user)
      setTeams(teams)

      // fetch all category
      const categories = await categoryService.getAllCategories(user)
      setCategories(
        categories.map((it) => {
          return { label: it.name, value: it.id }
        })
      )
    })()
  }, [user])

  const onSave = async () => {
    try {
      // save to database
      const initialDetails = {
        ...selectedProcess,
        process_template_name: processTemplate?.process_template_name,
        description: processTemplate?.description,
        prefix: processTemplate?.prefix,
        user_id: user?.id,
        company_id: user.company_id,
        category_ids: processTemplate?.selected_categories?.map((it) => it.value),
        allowed_initiator_team_ids: processTemplate?.allowed_initiator_team_ids
          ?.map((it) => it.id)
          .join(','),
        allowed_viewer_team_ids: processTemplate?.allowed_viewer_team_ids
          ?.map((it) => it.id)
          .join(','),
        is_tasks_hidden: processTemplate?.is_tasks_hidden === 'Yes' ? false : true,
      }

      // create new process in db
      await adminProcessService.createProcess(user, initialDetails)
      reloadProcesses()

      // finally hide dialog
      setSelectedProcess(null)
      setShow(false)
    } catch (error) {
      console.log('onProcessCreate', error)
    }
  }

  const handleChange = (event, newValue) => {
    setSelectedTabValue(newValue)
  }

  return (
    <DialogWrapper
      heading={'Process Template Setup'}
      open={show}
      onClose={() => setShow(false)}
      actions={null}
    >
      <TabContext value={selectedTabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleChange}
            aria-label="Process Template Setup"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="General" value="1" />
            <Tab label="Advance" value="2" />
          </TabList>
        </Box>
        <TabPanel value="1" sx={{ padding: 0, paddingTop: 2 }}>
          <TextField
            size="small"
            style={{ margin: '8px 0px' }}
            id="outlined"
            label="Process Name"
            placeholder="Process Name"
            sx={{ ...textFieldStyle, width: '100%' }}
            onChange={(e) => {
              setProcessTemplate({ ...processTemplate, process_template_name: e.target.value })
            }}
            value={processTemplate.process_template_name}
          />
          <TextField
            size="small"
            style={{ margin: '8px 0px' }}
            id="outlined"
            label="Process Prefix"
            placeholder="Process Prefix"
            sx={{ ...textFieldStyle, width: '100%' }}
            onChange={(e) => {
              setProcessTemplate({ ...processTemplate, prefix: e.target.value })
            }}
            value={processTemplate.prefix}
          />
          <TextField
            size="small"
            style={{ margin: '8px 0px' }}
            id="outlined"
            label="Description"
            placeholder="Description"
            sx={{ ...textFieldStyle, width: '100%' }}
            multiline
            onChange={(e) => {
              setProcessTemplate({ ...processTemplate, description: e.target.value })
            }}
            value={processTemplate?.description}
          />
          <Autocomplete
            multiple
            size="small"
            style={{ margin: '8px 0px' }}
            options={categories}
            sx={{ ...textFieldStyle, width: '100%' }}
            value={processTemplate?.selected_categories || []}
            onChange={(e, v) => {
              if (v) {
                setProcessTemplate({ ...processTemplate, selected_categories: v })
              }
            }}
            renderInput={(params) => <TextField {...params} label="Choose Category" />}
          />

          {/* grouped autocomplete for team selections */}
          <Autocomplete
            size="small"
            id="grouped-teams"
            options={teams.sort((a, b) => {
              if (a.team_type === 'internal') return -1
              if (b.team_type === 'internal') return 1
              return -b.team_type.localeCompare(a.team_type)
            })}
            groupBy={(option) => option.team_type}
            getOptionLabel={(option) => option.name}
            sx={{ ...textFieldStyle, width: '100%', mt: 2 }}
            multiple
            renderInput={(params) => (
              <TextField
                {...params}
                label="Allowed Viewer Team"
                placeholder="Allowed Viewer Team"
              />
            )}
            renderGroup={(params) => {
              return (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )
            }}
            value={processTemplate?.allowed_viewer_team_ids || []}
            onChange={(e, v) => {
              if (v) {
                setProcessTemplate({ ...processTemplate, allowed_viewer_team_ids: v })
              }
            }}
          />
          <Autocomplete
            size="small"
            id="grouped-teams"
            options={teams.sort((a, b) => {
              if (a.team_type === 'internal') return -1
              if (b.team_type === 'internal') return 1
              return -b.team_type.localeCompare(a.team_type)
            })}
            groupBy={(option) => option.team_type}
            getOptionLabel={(option) => option.name}
            sx={{ ...textFieldStyle, width: '100%', mt: 2 }}
            multiple
            renderInput={(params) => (
              <TextField
                {...params}
                label="Allowed Initiator Team"
                placeholder="Allowed Initiator Team"
              />
            )}
            renderGroup={(params) => {
              return (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )
            }}
            value={processTemplate?.allowed_initiator_team_ids || []}
            onChange={(e, v) => {
              if (v) {
                setProcessTemplate({ ...processTemplate, allowed_initiator_team_ids: v })
              }
            }}
          />
          <Autocomplete
            size="small"
            options={['Yes', 'No']}
            value={processTemplate?.is_tasks_hidden}
            onChange={(e, v) => {
              setProcessTemplate({
                ...processTemplate,
                is_tasks_hidden: v,
              })
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Visible in my tasks card?"
                placeholder="Select Yes or No"
                sx={{ ...textFieldStyle, margin: '8px 0px', width: '100%' }}
              />
            )}
          />
          <div
            style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', columnGap: 16 }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setSelectedProcess(null)
                setShow(false)
              }}
            >
              Cancel
            </Button>
            <Button color="primary" variant="contained" onClick={onSave}>
              Save
            </Button>
          </div>
        </TabPanel>
        <TabPanel value="2" sx={{ padding: 0, paddingTop: 2 }}>
          <ProtectedFieldSettingUI
            processTemplate={processTemplate}
            setSelectedProcess={setSelectedProcess}
            setShow={(isShowing) => {
              if (!isShowing) {
                setSelectedTabValue('1')
              }
              setShow(isShowing)
            }}
          />
        </TabPanel>
      </TabContext>
    </DialogWrapper>
  )
}

export default CreateProcessTemplateDialog
